import '../styles/CrawlsTable.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const STATUS_LABEL = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatStamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()} · ${hh}:${mm} UTC`;
}

function clampProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function DrawMark() {
  return (
    <svg className="cwt-head-mark" viewBox="0 0 28 10" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
      <path d="M1 6.5 C6 2.5 9 8.5 14 5 S22 1.5 27 4.5" />
    </svg>
  );
}

function SpiderGlyph() {
  return (
    <svg className="cwt-empty-mark" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" aria-hidden="true">
      <circle cx="24" cy="24" r="12" opacity="0.35" />
      <circle cx="24" cy="24" r="20" opacity="0.2" />
      <path d="M4 24 H44 M24 4 V44 M10 10 L38 38 M38 10 L10 38" opacity="0.2" />
      <circle cx="24" cy="24" r="3" fill="currentColor" stroke="none" />
      <path d="M22 21.5 c-3-3 -6-4.5 -9-5 M26 21.5 c3-3 6-4.5 9-5 M21.5 24 c-4 0-7 0.6-10 2 M26.5 24 c4 0 7 0.6 10 2 M22 26.5 c-3 3-5 5-6.5 8 M26 26.5 c3 3 5 5 6.5 8" />
    </svg>
  );
}

/* ── CrawlStatusBar subcomponent ── */
function CrawlStatusBar({ crawl }) {
  const status = String(crawl.status || 'queued').toLowerCase();
  const progress = clampProgress(crawl.progress);
  const count = Number.isFinite(Number(crawl.emailCount)) ? Number(crawl.emailCount) : 0;

  return (
    <div className="cwt-statusbar">
      <div className="cwt-bar">
        <span
          className="cwt-bar-track"
          role="progressbar"
          aria-label={`Crawl progress for ${crawl.target}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          aria-valuetext={`${progress} percent, ${count} emails found`}
        >
          <span className="cwt-bar-fill" style={{ width: `${progress}%` }} />
        </span>
        <span className="cwt-bar-count">{count} {count === 1 ? 'email' : 'emails'}</span>
      </div>
      <span className="cwt-bar-meta">{progress}% crawled</span>
    </div>
  );
}

/* ── CrawlFailedRowDetail subcomponent ── */
function CrawlFailedRowDetail({ crawl }) {
  const count = Number.isFinite(Number(crawl.emailCount)) ? Number(crawl.emailCount) : 0;
  return (
    <div className="cwt-failed-inner">
      <p className="cwt-failed-title">This crawl stopped before finishing.</p>
      <p className="cwt-failed-text">
        The crawl could not continue from its starting target. Pages it had already reached were
        skipped over; no further emails were extracted. The record is kept here so you can review
        where it stopped.
      </p>
      <dl className="cwt-failed-defs">
        <dt>Target</dt>
        <dd>{crawl.target}</dd>
        <dt>Stopped at</dt>
        <dd>{clampProgress(crawl.progress)}% · {count} {count === 1 ? 'email' : 'emails'} found</dd>
        <dt>Last update</dt>
        <dd>{formatStamp(crawl.updatedAt)}</dd>
        <dt>Crawl</dt>
        <dd>{crawl.id}</dd>
      </dl>
      <div className="cwt-actions">
        <a className="cwt-textlink" href="/new-crawl">Start a replacement crawl</a>
        <a className="cwt-textlink" href="/leads">Open Leads</a>
      </div>
    </div>
  );
}

function CrawlsSkeleton() {
  return (
    <div className="cwt-skeleton" aria-busy="true">
      <span className="cwt-skel-status" role="status">Loading your crawls…</span>
      {[1, 2, 3, 4].map((n) => (
        <div className="cwt-skel-row" key={`skel-${n}`} aria-hidden="true">
          <div className="cwt-skel-cell">
            <span className="cwt-skel-line" />
            <span className="cwt-skel-line cwt-skel-line--tiny" />
          </div>
          <div className="cwt-skel-cell"><span className="cwt-skel-line cwt-skel-line--short" /></div>
          <div className="cwt-skel-cell"><span className="cwt-skel-line" /></div>
          <div className="cwt-skel-cell"><span className="cwt-skel-line cwt-skel-line--short" /></div>
        </div>
      ))}
    </div>
  );
}

/* ── CrawlEmptyState subcomponent ── */
function CrawlEmptyState() {
  return (
    <div className="cwt-empty">
      <SpiderGlyph />
      <h3 className="cwt-empty-title">No crawls yet</h3>
      <p className="cwt-empty-text">
        dbaseextractor has not crawled any pages or web directories on your behalf. Give it a
        starting target and it will collect the email addresses it finds.
      </p>
      <a className="cwt-cta" href="/new-crawl">Start a new crawl</a>
    </div>
  );
}

/* ── CrawlErrorBanner subcomponent ── */
function CrawlErrorBanner({ message, onRetry }) {
  return (
    <div className="cwt-error" role="alert">
      <p className="cwt-error-text">
        <strong>Crawls could not be loaded.</strong> {message || 'The request did not complete. Your recorded crawls are unchanged.'}
      </p>
      <button type="button" className="cwt-retry" onClick={onRetry}>Retry</button>
    </div>
  );
}

function CrawlsTable() {
  const { data, loading, error, refresh } = useProjectCollection('crawls');
  const leadsState = useProjectCollection('leads');
  const [expandedCrawlId, setExpandedCrawlId] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const crawls = Array.isArray(data) ? data : [];
  const leads = Array.isArray(leadsState.data) ? leadsState.data : [];
  const hasError = Boolean(error) || Boolean(leadsState.error);

  const leadCountFor = (crawlId) => leads.filter((l) => l.crawlId === crawlId).length;

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await refresh();
      await leadsState.refresh();
    } finally {
      setRetrying(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedCrawlId((current) => (current === id ? null : id));
  };

  const showSkeleton = loading && crawls.length === 0;

  return (
    <section className="cwt-section" aria-labelledby="cwt-panel-label">
      <div className="cwt-shell">
        <div className="cwt-panel">
          <div className="cwt-head">
            <h2 className="cwt-head-label" id="cwt-panel-label">
              <DrawMark />
              Crawl history
            </h2>
            <p className="cwt-head-tally" aria-live="polite">
              {crawls.length} {crawls.length === 1 ? 'crawl' : 'crawls'} on record
            </p>
          </div>

          {hasError && !showSkeleton ? (
            <CrawlErrorBanner
              message={error?.message || leadsState.error?.message || ''}
              onRetry={handleRetry}
            />
          ) : null}

          {showSkeleton ? (
            <CrawlsSkeleton />
          ) : hasError && crawls.length === 0 ? null : crawls.length === 0 ? (
            <CrawlEmptyState />
          ) : (
            <div className="cwt-table-wrap">
              <table className="cwt-table">
                <colgroup>
                  <col className="cwt-col-target" />
                  <col className="cwt-col-status" />
                  <col className="cwt-col-progress" />
                  <col className="cwt-col-time" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Starting target</th>
                    <th scope="col">Status</th>
                    <th scope="col">Progress &amp; emails</th>
                    <th scope="col">Recorded</th>
                  </tr>
                </thead>
                <tbody>
                  {crawls.map((crawl) => {
                    const status = String(crawl.status || 'queued').toLowerCase();
                    const isExpanded = expandedCrawlId === crawl.id;
                    const detailOpen = status === 'failed' && isExpanded;
                    const detailId = `cwt-detail-${crawl.id}`;
                    const matchedLeads = leadCountFor(crawl.id);
                    return (
                      <React.Fragment key={crawl.id}>
                        <tr data-crawl-id={crawl.id}>
                          <td data-label="Target">
                            <span className="cwt-target">{crawl.target}</span>
                            <span className="cwt-target-id">{crawl.id}</span>
                            <div className="cwt-actions">
                              <a className="cwt-rowlink" href="/leads">
                                Open leads →
                              </a>
                              {status === 'failed' ? (
                                <button
                                  type="button"
                                  className="cwt-rowbtn"
                                  aria-expanded={detailOpen}
                                  aria-controls={detailId}
                                  onClick={() => toggleExpanded(crawl.id)}
                                >
                                  {detailOpen ? 'Hide failure detail' : 'Inspect failure'}
                                </button>
                              ) : (
                                <a className="cwt-rowbtn" href="/leads">
                                  Open crawl progress →
                                </a>
                              )}
                            </div>
                          </td>
                          <td data-label="Status">
                            <span className="cwt-status" data-status={status}>
                              {STATUS_LABEL[status] || 'Queued'}
                            </span>
                            <span className="cwt-bar-meta">
                              {matchedLeads} {matchedLeads === 1 ? 'lead' : 'leads'} stored
                            </span>
                          </td>
                          <td data-label="Progress & emails">
                            <CrawlStatusBar crawl={crawl} />
                          </td>
                          <td data-label="Recorded">
                            <span className="cwt-time">
                              <strong>Created</strong>
                              {formatStamp(crawl.createdAt)}
                            </span>
                            <span className="cwt-time">
                              <strong>Updated</strong>
                              {formatStamp(crawl.updatedAt)}
                            </span>
                          </td>
                        </tr>
                        {status === 'failed' ? (
                          <tr className="cwt-failed" id={detailId} hidden={!detailOpen}>
                            <td colSpan={4}>
                              <CrawlFailedRowDetail crawl={crawl} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="cwt-foot">
            <p className="cwt-foot-text">
              Crawls run in the background. Completed crawls keep their extracted emails on Leads.
            </p>
            <div className="cwt-foot-actions">
              <a className="cwt-rowlink" href="/leads">Open Leads</a>
              <a className="cwt-rowlink" href="/new-crawl">Start a new crawl</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CrawlsTable;